'use client';

import { useEffect } from 'react';

import { useAtom } from 'jotai';

import { mobileAppEmbedUrlAtom } from '@/shared-survey';
import { type GetProjectQuery } from '@/survey-graphql';

import { preloadMobileAppEmbedUrl } from './project-response-shared-effects.helpers';

type SurveyQuestion = GetProjectQuery['project']['questions'][number];

interface Options {
  questions: GetProjectQuery['project']['questions'];
  currentQuestionIndex: number;
}

interface Result {
  isPreloading: boolean;
}

function hasMobileAppEmbedUrl(q: SurveyQuestion): boolean {
  return Boolean(q.mobileAppEmbedUrl && q.mobileAppEmbedUrl.trim() !== '');
}

/** Warms the Appetize iframe one question before the upcoming Mobile App question. */
export function useMobileAppPreloadDriver({
  questions,
  currentQuestionIndex,
}: Options): Result {
  const [mobileAppEmbedUrl, setMobileAppEmbedUrl] = useAtom(
    mobileAppEmbedUrlAtom
  );

  const upcomingIndex = questions.findIndex(
    (q, idx) => idx > currentQuestionIndex && hasMobileAppEmbedUrl(q)
  );
  const upcoming = upcomingIndex >= 0 ? questions[upcomingIndex] : null;
  const triggerIndex = upcomingIndex > 0 ? upcomingIndex - 1 : -1;

  const shouldPreload =
    triggerIndex >= 0 &&
    currentQuestionIndex === triggerIndex &&
    !mobileAppEmbedUrl;

  const preloadUrl = upcoming?.mobileAppEmbedUrl ?? null;

  useEffect(
    () =>
      preloadMobileAppEmbedUrl({
        shouldPreload,
        preloadUrl,
        set: setMobileAppEmbedUrl,
      }),
    [shouldPreload, preloadUrl, setMobileAppEmbedUrl]
  );

  const isPreloading =
    !!mobileAppEmbedUrl &&
    !!upcoming?.mobileAppEmbedUrl &&
    mobileAppEmbedUrl === upcoming.mobileAppEmbedUrl &&
    upcomingIndex >= 0 &&
    currentQuestionIndex < upcomingIndex;

  return { isPreloading };
}
